// src/pages/statistics/DistributorStatisticsPage.tsx
import React, { useEffect, useState } from "react";
import PageHeader from "../../components/ui/PageHeader";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Field from "../../components/ui/Field";
import Input from "../../components/ui/Input";
import { Download } from "lucide-react";
import {
    getStatisticsFiltersRequest,
    getDistributorStatisticsRequest,
    exportDistributorStatisticsRequest,
    triggerBrowserDownload,
    StatsFilterDistributor,
    StatsRow,
    DistributorsStatisticsResponse,
} from "../../services/api/statistics.api";
import { formatMoney, formatDate, pluralizePaymentTypes } from "./statsFormat";

const DistributorStatisticsPage: React.FC = () => {
    const [distributors, setDistributors] = useState<StatsFilterDistributor[]>([]);
    const [filtersLoading, setFiltersLoading] = useState(true);
    const [filtersError, setFiltersError] = useState<string | null>(null);

    const [selectedDistributorId, setSelectedDistributorId] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const [data, setData] = useState<DistributorsStatisticsResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [pageError, setPageError] = useState<string | null>(null);

    const [isExporting, setIsExporting] = useState(false);
    const [exportMessage, setExportMessage] = useState<string | null>(null);

    useEffect(() => {
        (async () => {
            try {
                const res = await getStatisticsFiltersRequest();
                setDistributors(res.distributors);
            } catch (e) {
                setFiltersError(e instanceof Error ? e.message : "Не удалось загрузить фильтры");
            } finally {
                setFiltersLoading(false);
            }
        })();
    }, []);

    const loadStatistics = async () => {
        setIsLoading(true);
        setPageError(null);
        try {
            const res = await getDistributorStatisticsRequest({
                distributorId: selectedDistributorId || undefined,
                dateFrom: dateFrom || undefined,
                dateTo: dateTo || undefined,
            });
            setData(res);
        } catch (e) {
            setPageError(e instanceof Error ? e.message : "Не удалось загрузить статистику");
        } finally {
            setIsLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadStatistics();
    }, [selectedDistributorId, dateFrom, dateTo]);

    const handleExport = async () => {
        setIsExporting(true);
        setExportMessage(null);
        try {
            const result = await exportDistributorStatisticsRequest({
                distributorId: selectedDistributorId || undefined,
                dateFrom: dateFrom || undefined,
                dateTo: dateTo || undefined,
            });
            if (result.background) {
                setExportMessage(result.message || "Отчёт формируется в фоне и будет отправлен на почту.");
            } else if (result.blob && result.filename) {
                triggerBrowserDownload(result.blob, result.filename);
            }
        } catch (e) {
            setExportMessage(e instanceof Error ? e.message : "Не удалось выполнить выгрузку");
        } finally {
            setIsExporting(false);
        }
    };

    const renderStatsCells = (row: StatsRow, showAdminGiven: boolean) => (
        <>
            <td className="stats-cell-numeric">
                {row.orders_count}{" "}
                <span className="stats-cell-muted">({row.tickets_sold} бил.)</span>
            </td>
            <td className="stats-cell-numeric">{formatMoney(row.orders_sum)}</td>
            <td className="stats-cell-numeric">
                {row.unpaid_orders_count}{" "}
                <span className="stats-cell-muted">
                    ({row.unpaid_tickets_count} бил., {formatMoney(row.unpaid_sum)})
                </span>
            </td>
            <td className="stats-cell-numeric">
                {row.paid_orders_count}{" "}
                <span className="stats-cell-muted">
                    ({row.paid_tickets_count} бил., {formatMoney(row.paid_sum)})
                </span>
            </td>
            <td className="stats-cell-numeric">{row.invite_tickets_count}</td>
            <td className="stats-cell-numeric">{showAdminGiven ? row.admin_given_tickets_count : "—"}</td>
            <td>
                {row.payment_breakdown.length === 0 ? (
                    <span className="stats-cell-muted">—</span>
                ) : (
                    <details className="stats-payment-details">
                        <summary className="stats-payment-summary">
                            {pluralizePaymentTypes(row.payment_breakdown.length)}
                        </summary>
                        <div className="stats-payment-breakdown">
                            {row.payment_breakdown.map((pb) => (
                                <div className="stats-payment-line" key={pb.payment_type}>
                                    <span className="stats-payment-type">{pb.payment_type}</span>
                                    <span>
                                        Опл.: {pb.paid_orders_count} зак. / {pb.paid_count} бил.{" "}
                                        <span className="stats-cell-muted">({formatMoney(pb.paid_sum)})</span>
                                    </span>
                                    <span>
                                        Не опл.: {pb.unpaid_orders_count} зак. / {pb.unpaid_count} бил.{" "}
                                        <span className="stats-cell-muted">({formatMoney(pb.unpaid_sum)})</span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </details>
                )}
            </td>
        </>
    );

    return (
        <div className="page-content">
            <PageHeader
                title="Статистика по распространителям"
                description="Продажи и выданные билеты по каждому распространителю."
                actions={
                    <Button variant="secondary" onClick={handleExport} disabled={isExporting}>
                        <Download size={16} style={{ marginRight: 6 }} />
                        {isExporting ? "Формируем..." : "Выгрузить в Excel"}
                    </Button>
                }
            />

            <Card>
                <section className="section-toolbar">
                    <div>
                        <h3 className="card-title">Фильтры</h3>
                        <p className="card-text">
                            Выберите распространителя и период — таблица и выгрузка обновятся автоматически.
                        </p>
                    </div>
                </section>

                <form className="stats-filter-bar" onSubmit={(e) => e.preventDefault()}>
                    <div className="stats-filter-row">
                        <Field label="Распространитель" htmlFor="stats-distributor">
                            <select
                                id="stats-distributor"
                                className="ui-select"
                                value={selectedDistributorId}
                                onChange={(e) => setSelectedDistributorId(e.target.value)}
                                disabled={filtersLoading}
                            >
                                <option value="">
                                    {filtersLoading
                                        ? "Загрузка..."
                                        : filtersError
                                            ? "Ошибка загрузки"
                                            : "Все распространители"}
                                </option>
                                {distributors.map((d) => (
                                    <option key={d.id} value={d.id}>
                                        {d.name} ({d.email})
                                    </option>
                                ))}
                            </select>
                        </Field>

                        <Field label="Дата от" htmlFor="stats-dist-date-from">
                            <Input
                                id="stats-dist-date-from"
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                            />
                        </Field>

                        <Field label="Дата до" htmlFor="stats-dist-date-to">
                            <Input
                                id="stats-dist-date-to"
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                            />
                        </Field>
                    </div>
                </form>

                {exportMessage && <div className="stats-export-message">{exportMessage}</div>}
                {pageError && <div className="form-global-error">{pageError}</div>}

                {isLoading ? (
                    <div className="table-skeleton">
                        <div className="table-skeleton-row" />
                        <div className="table-skeleton-row" />
                        <div className="table-skeleton-row" />
                    </div>
                ) : !data || data.distributors.length === 0 ? (
                    <div className="empty-block">
                        <h3 className="empty-block-title">Данных нет</h3>
                        <p className="empty-block-text">
                            Нет распространителей с продажами за выбранный период.
                        </p>
                    </div>
                ) : (
                    <div className="table-wrap">
                        <table className="data-table stats-table">
                            <thead>
                            <tr>
                                <th>Распространитель / тур / город</th>
                                <th>Заказов</th>
                                <th>Сумма</th>
                                <th>Не оплачено</th>
                                <th>Оплачено</th>
                                <th>Пригласительные</th>
                                <th>Выдано админ-бронью</th>
                                <th>Оплаты по типам</th>
                            </tr>
                            </thead>
                            <tbody>
                            {data.distributors.map((distributor) => (
                                <React.Fragment key={distributor.id}>
                                    <tr className="stats-distributor-row">
                                        <td className="stats-row-name">
                                            {distributor.name}
                                            <span className="stats-cell-muted"> — {distributor.email}</span>
                                        </td>
                                        {renderStatsCells(distributor, true)}
                                    </tr>
                                    {distributor.tours.map((tour) => (
                                        <React.Fragment key={tour.id}>
                                            <tr className="stats-tour-row">
                                                <td className="stats-row-name" style={{ paddingLeft: 22 }}>
                                                    {tour.name}
                                                </td>
                                                {renderStatsCells(tour, false)}
                                            </tr>
                                            {tour.cities.map((city) => (
                                                <tr className="stats-city-row" key={city.id}>
                                                    <td className="stats-row-name">
                                                        {city.name}
                                                        {city.start_at && (
                                                            <span className="stats-cell-muted">
                                                                {" "}— {formatDate(city.start_at)}
                                                            </span>
                                                        )}
                                                    </td>
                                                    {renderStatsCells(city, false)}
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </React.Fragment>
                            ))}
                            <tr className="stats-total-row">
                                <td className="stats-row-name">{data.total.name}</td>
                                {renderStatsCells(data.total, true)}
                            </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default DistributorStatisticsPage;
